import { RatingFilled } from '@teko/ds-icon';
import {
  Body,
  Col,
  Flex,
  Heading,
  Row,
  ThemeProps,
  styled,
} from '@teko/ds-mobile';
import { WishlistButton } from 'components/WishlistButton';
import {
  useFeatureFlags,
  useProductDetailState,
  useTenantConfig,
} from 'contexts';
import { scrollSmoothToIdWithOffset } from 'helpers/page-actions';
import { t } from 'i18n';
import Link from 'next/link';
import React from 'react';

const shortenString = (name: string, maxLength = 20) => {
  const trimmedName = name?.trim();
  if (!trimmedName) return '';
  if (trimmedName.length <= maxLength) return trimmedName;
  return `${trimmedName.slice(0, maxLength)?.trimEnd()}...`;
};

const ProductBrand = ({
  brandCode,
  brandName,
}: {
  brandCode: string;
  brandName: string;
}) => {
  return (
    <Link
      href={{
        pathname: '/brands/[brandId]',
        query: {
          brandId: brandCode,
        },
      }}
      as={{
        pathname: `/${brandCode}-brand.${brandCode}`,
      }}
      shallow={true}
      passHref
      legacyBehavior
      prefetch={false}
    >
      <BrandNameTextLink>{shortenString(brandName)}</BrandNameTextLink>
    </Link>
  );
};

export const ProductInfo: React.FC = () => {
  const { epicOmni1550, isMobile } = useTenantConfig();
  const { flags } = useFeatureFlags();
  const { productRatingData, product } = useProductDetailState();
  const { totalReviews, averageRating } = productRatingData;

  if (!product) {
    return null;
  }

  return (
    <Flex direction="column" gap="s4">
      <Row>
        <Body level={1} color="textPrimary">
          {t('Thương hiệu')}:
        </Body>
        {flags.epic_omni_1552 && product?.brands?.length > 0 ? (
          product.brands.map((brand, index) => (
            <React.Fragment key={index}>
              <ProductBrand
                key={`${brand.code}${brand.name}${index}`}
                brandCode={brand.code}
                brandName={brand.name}
              />
              {index + 1 !== product?.brands?.length && ',\xa0'}
            </React.Fragment>
          ))
        ) : (
          <ProductBrand
            brandCode={product?.brand?.code}
            brandName={product?.brand?.name}
          />
        )}
      </Row>
      <Row align="top">
        <Col flex={isMobile ? '75%' : '100%'}>
          <ProductName
            isMobile={isMobile}
            level={1}
            color="textTitle"
            weight="bold"
          >
            {product.displayName || product.name}
          </ProductName>
        </Col>
        {isMobile && (
          <Col flex="25%">
            <Flex align="center" justify="end" gap="s4">
              <Body level={1} color="textTitle">
                {t('Yêu thích')}
              </Body>
              <WishlistButton sku={product.sku} size="24" />
            </Flex>
          </Col>
        )}
      </Row>
      <Row>
        <Body level={1}>
          <Body level={1} color="textPrimary">
            {t('SKU')}: {product.sellerSku || product.sku}
          </Body>
        </Body>
        {epicOmni1550 && !!totalReviews && (
          <Row align="middle">
            <Divider />
            <RatingFilled size={20} color="support" />
            <Body level={2} margin={['s0', 's4']}>
              {averageRating}
            </Body>
            <Body
              level={1}
              color="primary500"
              onClick={() =>
                scrollSmoothToIdWithOffset('review-section', isMobile ? 60 : 90)
              }
              style={{ cursor: 'pointer' }}
            >{`(${totalReviews} ${t('đánh giá')})`}</Body>
          </Row>
        )}
      </Row>
    </Flex>
  );
};

const ProductName = styled(Heading)<{ isMobile: boolean }>`
  ${({ isMobile }) =>
    isMobile
      ? `
    font-size: 20px;
    line-height: 28px;
    `
      : `
    font-size: 24px;
    line-height: 32px;
    `}
  margin: 0;
  text-align: left;
`;

const BrandNameTextLink = styled.a(
  ({ theme }: ThemeProps) => `
  color: ${theme?.colors?.primary};
  font-weight: normal;
  margin-left: 4px;
  font-size: 13px;
  line-height: 20px;
  text-decoration: none;
`,
);

const Divider = styled.div`
  ::before {
    content: '|';
    margin: 0 24px;
    color: #0000000f;
  }
`;
